import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as CalendarIcon, Plus, Edit, Trash2, Send, Users, Gift, TrendingUp, Award } from 'lucide-react';
import './AdventCalendar.css';

const AdventCalendar = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [winners, setWinners] = useState([]);
  const [samples, setSamples] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [showWinnerSelectionModal, setShowWinnerSelectionModal] = useState(false);
  const [eligibleSubscribers, setEligibleSubscribers] = useState([]);
  const [selectedMilestoneForWinners, setSelectedMilestoneForWinners] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';
  const [isFixingIndexes, setIsFixingIndexes] = useState(false);

  // Fix milestone indexes (maintenance function)
  const fixMilestoneIndexes = async () => {
    if (!window.confirm('This will fix database indexes for milestones. Continue?')) {
      return;
    }
    
    setIsFixingIndexes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/maintenance/fix-indexes`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Indexes fixed successfully! You can now create milestones.');
        console.log('Index fix result:', data);
      } else {
        alert('Error fixing indexes: ' + data.message);
      }
    } catch (error) {
      console.error('Error fixing indexes:', error);
      alert('Error fixing indexes: ' + error.message);
    } finally {
      setIsFixingIndexes(false);
    }
  };

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns);
        if (data.campaigns.length > 0 && !selectedCampaign) {
          setSelectedCampaign(data.campaigns[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, [API_URL, selectedCampaign]);

  // Fetch milestones
  const fetchMilestones = useCallback(async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns/${campaignId}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMilestones(data.milestones);
      }
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  }, [API_URL]);

  // Fetch winners
  const fetchWinners = useCallback(async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns/${campaignId}/winners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setWinners(data.winners);
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
    }
  }, [API_URL]);

  // Fetch samples for dropdown
  const fetchSamples = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/samples?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSamples(data.samples);
      }
    } catch (error) {
      console.error('Error fetching samples:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchCampaigns();
    fetchSamples();
  }, [fetchCampaigns, fetchSamples]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchMilestones(selectedCampaign._id);
      fetchWinners(selectedCampaign._id);
    }
  }, [selectedCampaign, fetchMilestones, fetchWinners]);

  // Campaign CRUD
  const createOrUpdateCampaign = async (campaignData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingCampaign
        ? `${API_URL}/api/advent/campaigns/${editingCampaign._id}`
        : `${API_URL}/api/advent/campaigns`;
      const method = editingCampaign ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(campaignData)
      });

      const data = await response.json();
      if (data.success) {
        alert(editingCampaign ? 'Campaign updated!' : 'Campaign created!');
        fetchCampaigns();
        setShowCampaignModal(false);
        setEditingCampaign(null);
      } else {
        alert(data.message || 'Error saving campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Error saving campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign and all its milestones? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        alert('Campaign deleted');
        fetchCampaigns();
        if (selectedCampaign?._id === campaignId) {
          setSelectedCampaign(null);
        }
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  // Milestone CRUD
  const createOrUpdateMilestone = async (milestoneData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingMilestone
        ? `${API_URL}/api/advent/milestones/${editingMilestone._id}`
        : `${API_URL}/api/advent/campaigns/${selectedCampaign._id}/milestones`;
      const method = editingMilestone ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(milestoneData)
      });

      const data = await response.json();
      if (data.success) {
        alert(editingMilestone ? 'Milestone updated!' : 'Milestone created!');
        fetchMilestones(selectedCampaign._id);
        setShowMilestoneModal(false);
        setEditingMilestone(null);
      } else {
        alert(data.message || 'Error saving milestone');
      }
    } catch (error) {
      console.error('Error saving milestone:', error);
      alert('Error saving milestone');
    }
  };

  const deleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestoneId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        alert('Milestone deleted');
        fetchMilestones(selectedCampaign._id);
      }
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  // Mark winners (new function)
  const markWinners = async (milestoneId) => {
    try {
      // First, fetch eligible subscribers
      const token = localStorage.getItem('token');
      const eligibleResponse = await fetch(`${API_URL}/api/advent/milestones/${milestoneId}/eligible`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const eligibleData = await eligibleResponse.json();
      if (!eligibleData.success || eligibleData.subscribers.length === 0) {
        alert('No eligible subscribers found for this milestone');
        return;
      }
      
      // Show modal with eligible subscribers
      setEligibleSubscribers(eligibleData.subscribers);
      setSelectedMilestoneForWinners(milestoneId);
      setShowWinnerSelectionModal(true);
    } catch (error) {
      console.error('Error fetching eligible subscribers:', error);
      alert('Error fetching eligible subscribers');
    }
  };

  // Confirm winner selection
  const confirmWinners = async (selectedSubscriberIds) => {
    if (selectedSubscriberIds.length === 0) {
      alert('Please select at least one winner');
      return;
    }

    if (!window.confirm(`Mark ${selectedSubscriberIds.length} subscriber(s) as winners?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${selectedMilestoneForWinners}/mark-winners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ subscriberIds: selectedSubscriberIds })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Marked ${data.results.marked} subscriber(s) as winners!`);
        setShowWinnerSelectionModal(false);
        fetchMilestones(selectedCampaign._id);
        fetchWinners(selectedCampaign._id);
      } else {
        alert(data.message || 'Error marking winners');
      }
    } catch (error) {
      console.error('Error marking winners:', error);
      alert('Error marking winners');
    }
  };

  return (
    <div className="advent-calendar-page">
      <div className="page-header">
        <div className="header-content">
          <CalendarIcon size={32} />
          <div>
            <h1>Advent Calendar Campaigns</h1>
            <p>Manage engagement-based prize campaigns with milestone tracking</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={fixMilestoneIndexes}
            disabled={isFixingIndexes}
            title="Fix database indexes if you're having trouble creating milestones"
          >
            {isFixingIndexes ? 'Fixing...' : '🔧 Fix Indexes'}
          </button>
          <button className="btn btn-primary" onClick={() => { setEditingCampaign(null); setShowCampaignModal(true); }}>
            <Plus size={20} />
            New Campaign
          </button>
        </div>
      </div>

      {/* Campaign Selector */}
      <div className="campaign-selector">
        <label>Active Campaign:</label>
        <select
          value={selectedCampaign?._id || ''}
          onChange={(e) => {
            const campaign = campaigns.find(c => c._id === e.target.value);
            setSelectedCampaign(campaign);
          }}
        >
          <option value="">Select a campaign...</option>
          {campaigns.map(campaign => (
            <option key={campaign._id} value={campaign._id}>
              {campaign.name} {campaign.isActive ? '(Active)' : '(Inactive)'}
            </option>
          ))}
        </select>
      </div>

      {selectedCampaign && (
        <>
          {/* Campaign Info Card */}
          <div className="campaign-info-card">
            <div className="info-header">
              <h2>{selectedCampaign.name}</h2>
              <div className="info-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setEditingCampaign(selectedCampaign); setShowCampaignModal(true); }}
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => deleteCampaign(selectedCampaign._id)}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
            <div className="info-details">
              <div className="info-item">
                <span className="label">Start Date:</span>
                <span className="value">{new Date(selectedCampaign.startDate).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="label">End Date:</span>
                <span className="value">{new Date(selectedCampaign.endDate).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="label">Status:</span>
                <span className={`badge ${selectedCampaign.isActive ? 'badge-success' : 'badge-secondary'}`}>
                  {selectedCampaign.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'campaigns' ? 'active' : ''}`}
              onClick={() => setActiveTab('campaigns')}
            >
              <Gift size={18} />
              Milestones
            </button>
            <button
              className={`tab ${activeTab === 'winners' ? 'active' : ''}`}
              onClick={() => setActiveTab('winners')}
            >
              <Award size={18} />
              Winners
            </button>
            <button
              className={`tab ${activeTab === 'streaks' ? 'active' : ''}`}
              onClick={() => setActiveTab('streaks')}
            >
              <TrendingUp size={18} />
              Subscriber Streaks
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'campaigns' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Campaign Milestones</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setEditingMilestone(null); setShowMilestoneModal(true); }}>
                  <Plus size={16} />
                  Add Milestone
                </button>
              </div>

              <div className="milestones-grid">
                {milestones.length === 0 ? (
                  <div className="empty-state">
                    <CalendarIcon size={48} />
                    <p>No milestones yet. Create your first milestone to start the campaign!</p>
                  </div>
                ) : (
                  milestones.map(milestone => (
                    <MilestoneCard
                      key={milestone._id}
                      milestone={milestone}
                      onEdit={() => { setEditingMilestone(milestone); setShowMilestoneModal(true); }}
                      onDelete={() => deleteMilestone(milestone._id)}
                      onSendPrizes={() => markWinners(milestone._id)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'winners' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Prize Winners</h3>
              </div>

              <div className="winners-table">
                {winners.length === 0 ? (
                  <div className="empty-state">
                    <Award size={48} />
                    <p>No winners yet. Prizes will appear here once milestones are reached.</p>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Subscriber</th>
                        <th>Prize</th>
                        <th>Milestone Streak</th>
                        <th>Total Campaign Streak</th>
                        <th>Date Awarded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {winners.map(winner => (
                        <tr key={winner._id}>
                          <td>
                            {winner.subscriber?.name || `${winner.subscriber?.firstName} ${winner.subscriber?.lastName}` || winner.subscriber?.email}
                          </td>
                          <td>{winner.milestone?.prizeTitle}</td>
                          <td>
                            <span className="badge badge-info">{winner.milestoneStreak} days</span>
                          </td>
                          <td>
                            <span className="badge badge-success">{winner.totalCampaignStreak} days</span>
                          </td>
                          <td>{new Date(winner.awardedAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {activeTab === 'streaks' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Campaign Milestone Statistics</h3>
                <p style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                  View subscriber engagement per milestone
                </p>
              </div>

              <div className="milestones-stats">
                {milestones.length === 0 ? (
                  <div className="empty-state">
                    <TrendingUp size={48} />
                    <p>No milestones yet. Create milestones to see engagement statistics.</p>
                  </div>
                ) : (
                  <div className="milestones-stats-grid">
                    {milestones.map(milestone => (
                      <MilestoneStatsCard
                        key={milestone._id}
                        milestone={milestone}
                        campaignId={selectedCampaign._id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <CampaignModal
          campaign={editingCampaign}
          onClose={() => { setShowCampaignModal(false); setEditingCampaign(null); }}
          onSave={createOrUpdateCampaign}
        />
      )}

      {/* Milestone Modal */}
      {showMilestoneModal && selectedCampaign && (
        <MilestoneModal
          milestone={editingMilestone}
          campaign={selectedCampaign}
          samples={samples}
          onClose={() => { setShowMilestoneModal(false); setEditingMilestone(null); }}
          onSave={createOrUpdateMilestone}
        />
      )}

      {/* Winner Selection Modal */}
      {showWinnerSelectionModal && (
        <WinnerSelectionModal
          eligibleSubscribers={eligibleSubscribers}
          onClose={() => setShowWinnerSelectionModal(false)}
          onConfirm={confirmWinners}
        />
      )}
    </div>
  );
};

// Milestone Card Component
const MilestoneCard = ({ milestone, onEdit, onDelete, onSendPrizes }) => {
  const milestoneStartDate = new Date(milestone.milestoneStartDate);
  const milestoneEndDate = new Date(milestone.milestoneEndDate);
  const isPast = milestoneEndDate < new Date();
  const eligibleCount = milestone.eligibleCount || 0;
  const winnerCount = milestone.winnerCount || 0;
  const API_URL = process.env.REACT_APP_API_URL || '';

  const handleExportSubscribers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestone._id}/export-subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `milestone_${milestone.prizeTitle.replace(/[^a-z0-9]/gi, '_')}_subscribers.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error exporting subscribers:', error);
      alert('Failed to export subscribers');
    }
  };

  const handleExportWinners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestone._id}/export-winners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `milestone_${milestone.prizeTitle.replace(/[^a-z0-9]/gi, '_')}_winners.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error exporting winners:', error);
      alert('Failed to export winners');
    }
  };

  // Calculate required days
  const calculateDays = (start, end) => {
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };
  
  const requiredDays = calculateDays(milestoneStartDate, milestoneEndDate);

  return (
    <div className="milestone-card">
      <div className="milestone-date">
        <div className="date-display">
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
            {milestoneStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>to</div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
            {milestoneEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </div>
        <div className="streak-req">
          <span className="label">Requires:</span>
          <span className="value">{requiredDays} {requiredDays === 1 ? 'day' : 'days'}</span>
        </div>
      </div>

      <div className="milestone-content">
        <h4>{milestone.prizeTitle}</h4>

        <div className="milestone-meta">
          <div className="meta-item">
            <Users size={14} />
            <span>{eligibleCount} eligible</span>
          </div>
          <div className="meta-item">
            <Gift size={14} />
            <span>{winnerCount} marked as winners</span>
          </div>
        </div>

        {winnerCount > 0 && (
          <div className="sent-badge">
            ✓ {winnerCount} Winner(s) Marked
          </div>
        )}
      </div>

      <div className="milestone-actions">
        <button className="btn btn-secondary btn-sm" onClick={onEdit} title="Edit Milestone">
          <Edit size={14} />
        </button>
        <button
          className="btn btn-info btn-sm"
          onClick={handleExportSubscribers}
          title="Export All Subscriber Streaks"
        >
          📊 Export Streaks
        </button>
        {winnerCount > 0 && (
          <button
            className="btn btn-success btn-sm"
            onClick={handleExportWinners}
            title="Export Winners"
          >
            📥 Export Winners
          </button>
        )}
        {isPast && eligibleCount > 0 && (
          <button
            className="btn btn-primary btn-sm"
            onClick={onSendPrizes}
            title="Mark Winners"
          >
            <Send size={14} />
            Mark Winners
          </button>
        )}
        <button className="btn btn-danger btn-sm" onClick={onDelete} title="Delete Milestone">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// Campaign Modal Component
const CampaignModal = ({ campaign, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    description: campaign?.description || '',
    startDate: campaign?.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
    endDate: campaign?.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
    isActive: campaign?.isActive !== undefined ? campaign.isActive : true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{campaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Campaign Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span>Campaign Active</span>
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {campaign ? 'Update' : 'Create'} Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Milestone Modal Component
const MilestoneModal = ({ milestone, campaign, samples, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    milestoneStartDate: milestone?.milestoneStartDate ? new Date(milestone.milestoneStartDate).toISOString().split('T')[0] : '',
    milestoneEndDate: milestone?.milestoneEndDate ? new Date(milestone.milestoneEndDate).toISOString().split('T')[0] : '',
    prizeTitle: milestone?.prizeTitle || ''
  });

  // Calculate required days
  const calculateRequiredDays = () => {
    if (!formData.milestoneStartDate || !formData.milestoneEndDate) return 0;
    const start = new Date(formData.milestoneStartDate);
    const end = new Date(formData.milestoneEndDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : 0;
  };

  const requiredDays = calculateRequiredDays();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (new Date(formData.milestoneStartDate) >= new Date(formData.milestoneEndDate)) {
      alert('End date must be after start date');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{milestone ? 'Edit Milestone' : 'Create New Milestone'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.milestoneStartDate}
                onChange={(e) => setFormData({ ...formData, milestoneStartDate: e.target.value })}
                min={new Date(campaign.startDate).toISOString().split('T')[0]}
                max={new Date(campaign.endDate).toISOString().split('T')[0]}
                required
              />
              <small>First day of milestone period</small>
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={formData.milestoneEndDate}
                onChange={(e) => setFormData({ ...formData, milestoneEndDate: e.target.value })}
                min={formData.milestoneStartDate || new Date(campaign.startDate).toISOString().split('T')[0]}
                max={new Date(campaign.endDate).toISOString().split('T')[0]}
                required
              />
              <small>Last day of milestone period</small>
            </div>
          </div>

          {requiredDays > 0 && (
            <div className="alert alert-success" style={{ marginTop: '10px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px', border: '1px solid #4caf50' }}>
              <strong>Required Engagement:</strong> Subscribers must engage {requiredDays} {requiredDays === 1 ? 'day' : 'days'} 
              ({new Date(formData.milestoneStartDate).toLocaleDateString()} to {new Date(formData.milestoneEndDate).toLocaleDateString()})
            </div>
          )}

          <div className="form-group">
            <label>Prize Title *</label>
            <input
              type="text"
              value={formData.prizeTitle}
              onChange={(e) => setFormData({ ...formData, prizeTitle: e.target.value })}
              placeholder="e.g., Week 1 Prize, 5-Day Challenge"
              required
            />
            <small>Used to identify this milestone and its winners</small>
          </div>

          <div className="alert alert-info" style={{ marginTop: '20px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
            <strong>Note:</strong> To win, subscribers must engage (listen or download) EVERY DAY from start to end date. Each milestone period starts fresh at 0.
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {milestone ? 'Update' : 'Create'} Milestone
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Winner Selection Modal Component
const WinnerSelectionModal = ({ eligibleSubscribers, onClose, onConfirm }) => {
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleToggleSubscriber = (subscriberId) => {
    setSelectedSubscribers(prev => {
      if (prev.includes(subscriberId)) {
        return prev.filter(id => id !== subscriberId);
      } else {
        return [...prev, subscriberId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(eligibleSubscribers.map(s => s._id.toString()));
    }
    setSelectAll(!selectAll);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Winners</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p><strong>{eligibleSubscribers.length}</strong> eligible subscriber(s) found</p>
            <label className="checkbox-label" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
              <span>Select All</span>
            </label>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table style={{ width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5' }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Select</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Milestone Streak</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Total Campaign Streak</th>
                </tr>
              </thead>
              <tbody>
                {eligibleSubscribers.map(subscriber => (
                  <tr key={subscriber._id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.includes(subscriber._id.toString())}
                        onChange={() => handleToggleSubscriber(subscriber._id.toString())}
                      />
                    </td>
                    <td style={{ padding: '10px' }}>{subscriber.email}</td>
                    <td style={{ padding: '10px' }}>
                      {subscriber.name || `${subscriber.firstName || ''} ${subscriber.lastName || ''}`.trim() || '-'}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span className="badge badge-info">{subscriber.milestoneStreak} days</span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span className="badge badge-success">{subscriber.totalCampaignStreak} days</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '15px', padding: '10px', background: '#f0f0f0', borderRadius: '4px' }}>
            <strong>{selectedSubscribers.length}</strong> subscriber(s) selected
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={() => onConfirm(selectedSubscribers)}
            disabled={selectedSubscribers.length === 0}
          >
            Mark {selectedSubscribers.length} as Winner(s)
          </button>
        </div>
      </div>
    </div>
  );
};

// Milestone Stats Card Component
const MilestoneStatsCard = ({ milestone, campaignId }) => {
  const [expanded, setExpanded] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || '';

  const loadStats = async () => {
    if (stats) {
      setExpanded(!expanded);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestone._id}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
        setExpanded(true);
      }
    } catch (error) {
      console.error('Error loading milestone stats:', error);
      alert('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSubscribers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestone._id}/export-subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `milestone_${milestone.prizeTitle.replace(/[^a-z0-9]/gi, '_')}_subscribers.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error exporting subscribers:', error);
      alert('Failed to export subscribers');
    }
  };

  const milestoneDate = new Date(milestone.milestoneDate);
  const eligibleCount = milestone.eligibleCount || 0;
  const winnerCount = milestone.winnerCount || 0;

  return (
    <div className="milestone-stats-card">
      <div className="stats-card-header" onClick={loadStats} style={{ cursor: 'pointer' }}>
        <div className="stats-card-title">
          <h4>{milestone.prizeTitle}</h4>
          <span className="stats-card-date">
            {milestoneDate.toLocaleDateString()} • Requires {milestone.requiredStreak} day streak
          </span>
        </div>
        <div className="stats-card-summary">
          <div className="stat-item">
            <span className="stat-label">Eligible</span>
            <span className="stat-value">{eligibleCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Winners</span>
            <span className="stat-value">{winnerCount}</span>
          </div>
          <button className="btn btn-sm btn-secondary" style={{ marginLeft: '10px' }}>
            {loading ? '...' : expanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {expanded && stats && (
        <div className="stats-card-details">
          <div className="stats-summary">
            <div className="summary-item">
              <span className="summary-label">Total Subscribers Tracked:</span>
              <span className="summary-value">{stats.totalSubscribers}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Met Requirement:</span>
              <span className="summary-value">{stats.metRequirement}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Average Milestone Streak:</span>
              <span className="summary-value">{stats.avgMilestoneStreak.toFixed(1)} days</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Average Total Streak:</span>
              <span className="summary-value">{stats.avgTotalStreak.toFixed(1)} days</span>
            </div>
          </div>

          <div className="stats-actions">
            <button className="btn btn-primary btn-sm" onClick={handleExportSubscribers}>
              📊 Export All Subscriber Data
            </button>
          </div>

          <div className="stats-table-container">
            <h5>Top Performers (by Milestone Streak)</h5>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Milestone Streak</th>
                  <th>Total Campaign Streak</th>
                  <th>Winner</th>
                </tr>
              </thead>
              <tbody>
                {stats.topPerformers.slice(0, 10).map((subscriber, idx) => (
                  <tr key={idx}>
                    <td>{subscriber.email}</td>
                    <td>{subscriber.name || '-'}</td>
                    <td>
                      <span className="badge badge-info">{subscriber.milestoneStreak} days</span>
                    </td>
                    <td>
                      <span className="badge badge-success">{subscriber.totalCampaignStreak} days</span>
                    </td>
                    <td>
                      {subscriber.isWinner ? (
                        <span className="badge badge-success">✓ Yes</span>
                      ) : (
                        <span className="badge badge-secondary">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdventCalendar;

